import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    Typography,
    Box,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    useMediaQuery,
    Drawer,
    CircularProgress,
    Stack,
    Button
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'components/snackbar';
import { setJobSearchStatus, getBasicProfileDetails } from 'api/users';

// This maps the BACKEND value (from user object) to the FRONTEND value (for the radio button)
const statusReverseMap = {
    high: 'urgent',
    medium: 'passive',
    low: 'exceptional',
    none: 'not_looking',
};

const statusOptions = [
    {
        value: 'urgent',
        primary: 'I am actively searching for a new job',
        secondary: '(We are on it. You’ll hear from us frequently)',
    },
    {
        value: 'passive',
        primary: 'I’m open to interesting opportunities but not actively searching',
        secondary: '(We’ll send you suitable job opportunities a couple of times each month.)',
    },
    // {
    //     value: 'exceptional',
    //     primary: 'I’ll consider only if something exceptional comes along',
    //     secondary: '(We will share only highly suitable jobs once in a while)',
    // },
    {
        value: 'not_looking',
        primary: 'Not looking out. Have a great job already',
        secondary: '(That’s wonderful to hear. Update your search option when you need help)',
    },
];

/**
 * A dialog component that lets the user set their job search status.
 * The component takes in three props:
 * - open: A boolean indicating whether the dialog should be open or not.
 * - onClose: A function that is called when the dialog should close.
 * - currentUserStatus: The current job search status of the user as a string.
 * The component renders a dialog with a radio button group with four options.
 * When the user selects an option, the component updates the job search status
 * with the selected option and notifies the user of the result with a snackbar.
 * If the update fails, the component displays an error snackbar.
 * If the user is on a mobile device, the component renders a drawer instead of a dialog.
 * When the user closes the dialog, the component calls the onClose function.
 * @param {{ open: boolean, onClose: function, currentUserStatus: string }} props
 * @returns A JSX element representing the dialog component.
 */
const JobSearchStatusDialog = ({ open, onClose, currentUserStatus }) => {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [selectedValue, setSelectedValue] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        fetchCandidate();
    }, [open]);

    const fetchCandidate = async () => {
        try {
            setIsSaving(true);
            const response = await getBasicProfileDetails();
            if (response?.success) {
                const jobSearchStatus = response?.data?.candidate?.job_search_status;
                console.log(jobSearchStatus);

                const frontendStatus = statusReverseMap[jobSearchStatus] || 'urgent';
                setSelectedValue(frontendStatus);
            } else {
                console.error('Failed to fetch candidate details:', response?.message || 'Unknown error');
            }

        } catch (error) {
            console.error('Network or server error while fetching candidate details:', error);
            // enqueueSnackbar('A network error occurred. Please try again.', { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Handles the change of the radio button status.
     * Updates the job search status with the selected option and notifies the user of the result with a snackbar.
     * If the update fails, the component displays an error snackbar.
     * If the user is on a mobile device, the component renders a drawer instead of a dialog.
     * When the user closes the dialog, the component calls the onClose function.
     * @param {object} event The event object from the radio button change action.
     */
    const handleRadioChange = (event) => {
        const newStatus = event.target.value;
        setSelectedValue(newStatus);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const response = await setJobSearchStatus({ status: selectedValue });
            if (response.success) {
                enqueueSnackbar('Job search status updated!', { variant: 'success' });
                setTimeout(() => {
                    onClose();
                }, 1000);
            } else {
                enqueueSnackbar(response.message || 'Failed to update status.', { variant: 'error' });
            }
        } catch (error) {
            console.error("Error saving job status:", error);
            enqueueSnackbar('A network error occurred. Please try again.', { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const renderDialogContent = (
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box mb={1} textAlign="center">
                <Typography variant="h5" fontWeight="bold">Job Search Status</Typography>
                <Typography variant="body1" color="text.secondary">(select the option applicable to you)</Typography>
            </Box>

            <Box sx={{ my: 3, position: 'relative' }}>
                {isSaving && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                        borderRadius: '8px'
                    }}>
                        <CircularProgress />
                    </Box>
                )}
                <FormControl fullWidth>
                    <RadioGroup value={selectedValue} onChange={handleRadioChange}>
                        {statusOptions.map((option) => (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<Radio disabled={isSaving} />}
                                className={selectedValue === option.value ? "active-radio" : ""}
                                label={
                                    <Box>
                                        <Typography className="option-title">{option.primary}</Typography>
                                        <Typography className="option-text" color="text.secondary" variant="body2">{option.secondary}</Typography>
                                    </Box>
                                }
                                sx={{ mb: 2, alignItems: 'flex-start' }}
                                disabled={isSaving}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            </Box>
            <Stack direction={{ xs: 'column-reverse', md: 'row' }} alignItems={'center'} spacing={2.5}>
                <Button fullWidth variant="outlined" onClick={() => onClose()}>
                    Cancel
                </Button>
                <Button fullWidth variant="contained" onClick={() => handleSave()}>
                    Done
                </Button>
            </Stack>
        </DialogContent>
    );

    if (isMobile) {
        return (
            <Drawer
                anchor="bottom"
                open={open}
                onClose={onClose}
                slotProps={{ paper: { sx: { borderTopLeftRadius: '16px', borderTopRightRadius: '16px' } } }}
            >
                {renderDialogContent}
            </Drawer>
        );
    }

    return (
        <Dialog
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="otp-dialog-wrapper"
            open={open}
            onClose={onClose}
        >
            {renderDialogContent}
        </Dialog>
    );
};

export default JobSearchStatusDialog;